// zcta.js — ZIP Code Tabulation Areas that INTERSECT a drawn polygon (inside OR
// touching the perimeter), from the free Census TIGERweb service. CORS-enabled +
// no API key, so the browser calls it directly. Layer 2 = "2020 Census ZCTAs".
const ZCTA_URL = "https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_Current/MapServer/2/query";

// A freehand lasso produces hundreds of points; thin it so the request stays small.
// The boundary result is unaffected — Census returns generalized ZCTA shapes.
export function decimatePath(pts, max = 120) {
  if (!pts || pts.length <= max) return pts || [];
  const step = pts.length / max, out = [];
  for (let i = 0; i < pts.length; i += step) out.push(pts[Math.floor(i)]);
  return out;
}

// [{lat,lng}] → a closed esri ring of [lng,lat].
export function ringFromPath(pts) {
  const ring = pts.map((p) => [p.lng, p.lat]);
  if (ring.length && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) ring.push(ring[0]);
  return ring;
}

const parseFeatures = (d, geometry) =>
  (d.features || [])
    .map((f) => ({
      zip: String(f.attributes.ZCTA5 ?? f.attributes.BASENAME ?? "").trim(),
      paths: geometry && f.geometry ? f.geometry.rings.map((r) => r.map(([lng, lat]) => ({ lat, lng }))) : null,
    }))
    .filter((z) => /^\d{5}$/.test(z.zip));

// Boundaries for a KNOWN list of ZIPs (the operator's saved service area), so the
// map can show what they already service without anyone drawing anything.
// Chunked: a WHERE ... IN of 135 zips in one request is over Census's comfort zone,
// and a failed chunk should cost that chunk, not the whole map.
export async function zctasByCodes(zips, { chunk = 25 } = {}) {
  const clean = [...new Set((zips || []).map((z) => String(z).trim()).filter((z) => /^\d{5}$/.test(z)))];
  if (!clean.length) return [];
  const batches = [];
  for (let i = 0; i < clean.length; i += chunk) batches.push(clean.slice(i, i + chunk));
  const results = await Promise.all(batches.map(async (batch) => {
    const body = new URLSearchParams({
      where: `ZCTA5 IN (${batch.map((z) => `'${z}'`).join(",")})`,
      outFields: "ZCTA5,BASENAME",
      returnGeometry: "true",
      outSR: "4326",
      // ~1m precision; the shapes are generalized anyway and this roughly halves the payload.
      geometryPrecision: "5",
      f: "json",
    });
    const res = await fetch(ZCTA_URL, { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body });
    const d = await res.json();
    if (d.error) throw new Error(d.error.message || "Census ZCTA query failed");
    return parseFeatures(d, true);
  }));
  return results.flat();
}

// Returns [{ zip, paths }] where paths is [[{lat,lng}...]] for drawing on a Google
// map (null when geometry:false). Throws on a Census error.
export async function zctasInPolygon(pts, { geometry = true } = {}) {
  const ring = ringFromPath(decimatePath(pts));
  if (ring.length < 4) return [];
  const body = new URLSearchParams({
    geometry: JSON.stringify({ rings: [ring], spatialReference: { wkid: 4326 } }),
    geometryType: "esriGeometryPolygon",
    inSR: "4326",
    outSR: "4326",
    spatialRel: "esriSpatialRelIntersects",
    outFields: "ZCTA5,BASENAME",
    returnGeometry: geometry ? "true" : "false",
    f: "json",
  });
  const res = await fetch(ZCTA_URL, { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body });
  const d = await res.json();
  if (d.error) throw new Error(d.error.message || "Census ZCTA query failed");
  return parseFeatures(d, geometry);
}
